// Token storage for the Authorization Code + PKCE flow.
// Access + id tokens are short lived (1h); the refresh token lasts 7 days.
// Stored in localStorage: this is an internal staff tool behind Cognito MFA and
// CloudFront, and the app needs to survive full page reloads on the upload and
// callback routes.

const ACCESS_KEY = 'ip.accessToken';
const ID_KEY = 'ip.idToken';
const REFRESH_KEY = 'ip.refreshToken';
const EXPIRES_KEY = 'ip.expiresAt';

export interface StoredTokens {
  accessToken: string;
  idToken?: string;
  refreshToken?: string;
  expiresAt: number; // epoch ms
}

export function saveTokens(t: {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  expires_in: number;
}): void {
  localStorage.setItem(ACCESS_KEY, t.access_token);
  if (t.id_token) localStorage.setItem(ID_KEY, t.id_token);
  // Cognito omits refresh_token on a refresh grant; keep the existing one.
  if (t.refresh_token) localStorage.setItem(REFRESH_KEY, t.refresh_token);
  const expiresAt = Date.now() + t.expires_in * 1000;
  localStorage.setItem(EXPIRES_KEY, String(expiresAt));
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function getExpiresAt(): number {
  const raw = localStorage.getItem(EXPIRES_KEY);
  return raw ? Number(raw) : 0;
}

export function isExpired(skewMs = 30_000): boolean {
  return Date.now() + skewMs >= getExpiresAt();
}

export function hasSession(): boolean {
  return Boolean(getAccessToken() && getRefreshToken());
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(ID_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(EXPIRES_KEY);
}

export function getIdToken(): string | null {
  return localStorage.getItem(ID_KEY);
}

// Display claims from the id token payload. Decode only; signature verification
// happened at the API. Returns {} when absent or malformed.
export function getIdClaims(): { email?: string; name?: string } {
  const t = getIdToken();
  if (!t) return {};
  try {
    const payload = t.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return {};
  }
}
